import React from "react";
import { sameDay, parseTimeString, normalizeTimeFormat, addDays, QUADRANT_COLORS } from "../utils/calendarUtils";

const CalendarView = ({ selectedDate, appointments, onTimeSlotDoubleClick, onAppointmentClick, view = "day" }) => {
  // Full day time slots - hourly only for display
  const timeSlots = [
    "12:00AM", "1:00AM", "2:00AM", "3:00AM", "4:00AM", "5:00AM",
    "6:00AM", "7:00AM", "8:00AM", "9:00AM", "10:00AM", "11:00AM",
    "12:00PM", "1:00PM", "2:00PM", "3:00PM", "4:00PM", "5:00PM",
    "6:00PM", "7:00PM", "8:00PM", "9:00PM", "10:00PM", "11:00PM"
  ].sort((a, b) => {
    const [aHours, aMinutes] = parseTimeString(a);
    const [bHours, bMinutes] = parseTimeString(b);
    const aTotal = aHours * 60 + aMinutes;
    const bTotal = bHours * 60 + bMinutes;
    return aTotal - bTotal;
  });

  // Generate date range based on view
  const getDateRange = () => {
    switch (view) {
      case "3days":
        return [selectedDate, addDays(selectedDate, 1), addDays(selectedDate, 2)];
      case "week":
        const weekDays = [];
        const startOfWeek = new Date(selectedDate);
        const day = startOfWeek.getDay();
        const diff = startOfWeek.getDate() - day;
        startOfWeek.setDate(diff);
        for (let i = 0; i < 7; i++) {
          weekDays.push(addDays(startOfWeek, i));
        }
        return weekDays;
      case "day":
      default:
        return [selectedDate];
    }
  };

  const dateRange = getDateRange();

  // Helper function to normalize time format to "H:MMAM/PM"
  const normalizeTime = normalizeTimeFormat;

  const getAppointmentsForDate = (date) => {
    const dateAppointments = appointments.filter(apt => {
      if (!apt.dateTime || !apt.endTime) {
        return false;
      }
      
      // Check if appointment is on the selected date
      const aptDate = new Date(apt.dateTime);
      const dateOnly = new Date(date);
      dateOnly.setHours(0, 0, 0, 0);
      aptDate.setHours(0, 0, 0, 0);
      
      const isSameDay = aptDate.getTime() === dateOnly.getTime();
      return isSameDay;
    });

    return dateAppointments;
  };

  const getAppointmentPosition = (apt) => {
    const aptStartObj = new Date(apt.dateTime);
    const aptEndObj = new Date(apt.endTime);
    
    const startHours = aptStartObj.getHours();
    const startMinutes = aptStartObj.getMinutes();
    const aptStart = startHours * 60 + startMinutes;
    
    const endHours = aptEndObj.getHours();
    const endMinutes = aptEndObj.getMinutes();
    const aptEnd = endHours * 60 + endMinutes;
    
    // Calculate position as percentage of 24-hour day (1440 minutes)
    const topPercent = (aptStart / 1440) * 100;
    const heightPercent = ((aptEnd - aptStart) / 1440) * 100;
    
    return {
      topPercent,
      heightPercent
    };
  };

  return (
    <div className="time-grid-section">
      {/* <div className="instructions">
        <strong>Click on a time slot to add appointment. Click on existing appointments to edit.</strong>
        <div style={{fontSize: '12px', color: '#666', marginTop: '5px'}}>
          Showing {appointments.length} total appointments
        </div>
      </div> */}
      
      {/* Multi-Day View (Day, 3 Days, Week) */}
      <div className="multi-day-grid">
        <div className="time-column">
          <div className="time-header">Time</div>
          {timeSlots.map(time => (
            <div key={`time-${time}`} className="time-slot-label">{time}</div>
          ))}
        </div>
        
        {dateRange.map((date, dayIndex) => {
          const dayName = date.toLocaleDateString('en-US', { weekday: 'short' });
          const dayNum = date.getDate();
          const dayAppointments = getAppointmentsForDate(date);
          
          return (
            <div key={`day-${dayIndex}`} className="day-column-wrapper">
              <div className="day-column">
                <div className="day-header">
                  <div className="day-name">{dayName}</div>
                  <div className="day-date">{dayNum}</div>
                </div>
                <div className="day-time-slots-container">
                  {timeSlots.map(time => (
                    <div
                      key={`${dayIndex}-${time}`}
                      className="day-time-slot"
                      onClick={() => onTimeSlotDoubleClick(time, date)}
                    >
                      <span className="add-slot-icon" aria-hidden="true">
                        <svg viewBox="0 0 24 24" width="22" height="22" focusable="false">
                          <path d="M12 5v14M5 12h14" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                        </svg>
                      </span>
                    </div>
                  ))}
                </div>
                
                {/* Render appointments as overlays */}
                <div className="day-appointments-overlay">
                  {dayAppointments.map(apt => {
                    const { topPercent, heightPercent } = getAppointmentPosition(apt);
                    const colorConfig = QUADRANT_COLORS[apt.quadrant] || QUADRANT_COLORS["Not Urgent/Not Important"];
                    return (
                      <div
                        key={apt.id}
                        className="multi-day-appointment-absolute"
                        style={{
                          top: `${topPercent}%`,
                          height: `${heightPercent}%`,
                          minHeight: '32px',
                          background: colorConfig.color,
                          borderColor: colorConfig.border
                        }}
                        onClick={(e) => {
                          e.stopPropagation();
                          onAppointmentClick(apt);
                        }}
                        title={`Click to edit: ${apt.title}\nType: ${apt.type || 'Not specified'}`}
                      >
                        <div className="appointment-title">{apt.title}</div>
                        {apt.type && <div className="appointment-type">{apt.type}</div>}
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default CalendarView;